import type { Collection, CollectionDefaults, ResolvedCollection, StoreSchema } from '@rstore/shared';
import type { CrudActionType as CrudActionName } from '#layers/access/public';
import type { CustomCollectionMeta, PrimitivePrimaryKey } from '#layers/data-model/public';
import { joinURL } from 'ufo';
import { isNil } from '#layers/base/public';
import { hasCompositeKey } from './has-composite-key';

interface ApiEndpointOptions<TCollection extends Collection = Collection, TCollectionDefaults extends CollectionDefaults = CollectionDefaults, TSchema extends StoreSchema = StoreSchema> {
	collection: ResolvedCollection<TCollection, TCollectionDefaults, TSchema>;
	key?: PrimitivePrimaryKey;
	operation: CrudActionName;
}

/**
 * Builds the endpoint path for a collection, optionally scoped to a specific item.
 * The path is relative to the store's bound transport, which prefixes the API scope.
 * Only collections flagged `useItemsEndpoint` (user collections, flagged by schema hydration) fall back to
 * `items/<name>`; every other collection must declare its own `endpoint`, since the backend rejects system
 * collections on `/items`.
 */
export function apiEndpoint<TCollection extends Collection = Collection, TCollectionDefaults extends CollectionDefaults = CollectionDefaults, TSchema extends StoreSchema = StoreSchema>(options: ApiEndpointOptions<TCollection, TCollectionDefaults, TSchema>) {
	const { collection, key, operation } = options;
	const { endpoint, useItemsEndpoint } = collection.meta ?? {};
	let path = extractEndpointFromConfig(endpoint, !isNil(key), operation);

	if (!path) {
		if (!useItemsEndpoint) {
			throw new Error(`Collection ${collection.name} has no endpoint for ${operation}`);
		}

		path = `items/${collection.name}`;
	}

	if (collection.meta?.singleton || !hasItemKey(key)) {
		return path;
	}

	if (hasCompositeKey(collection)) {
		// TODO depending on the engine syntax
		throw new Error('not-implemented: composite keys in apiEndpoint');
	}

	return joinURL(path, String(key));
}

/**
 * Whether a read resolves to the collection's many endpoint and therefore answers with a list.
 * Singletons use their bare endpoint too, but it answers with the single object.
 */
export function readsListEndpoint(options: Pick<ApiEndpointOptions, 'collection' | 'key'>) {
	return !options.collection.meta?.singleton && !hasItemKey(options.key);
}

function hasItemKey(key: PrimitivePrimaryKey | undefined) {
	return !isNil(key) && !(typeof key === 'string' && key.length === 0);
}

function extractEndpointFromConfig(endpointConfig: CustomCollectionMeta['endpoint'] | undefined, single: boolean, action: CrudActionName) {
	if (!endpointConfig) {
		// don't return anything
	}
	else if (typeof endpointConfig === 'string') {
		return endpointConfig;
	}
	else if (typeof endpointConfig === 'object') {
		const forCardinality = endpointConfig[single ? 'single' : 'many'];

		if (forCardinality) {
			if (typeof forCardinality === 'string') {
				// single endpoint for all actions
				return forCardinality;
			}
			else if (typeof forCardinality === 'object') {
				return forCardinality[action];
			}
		}
	}
}
